// DS3.9
import React, { useCallback, useRef } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import {
  BiData,
  SearchClickBiEvent,
  TopNavClickBiEvent,
} from '@wix/da-bi/pkg/events';
import InputField from '@wix/da-ds/pkg/formControls/lego/InputField';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import SearchIcon from '@wix/da-ds/pkg/Icons/24x24/Search';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { USERNAME_REGEX } from '../../../User/helpers';
import s from './HeaderSearchInput.scss';

export interface Props {
  className?: string;
  searchQuery?: string;
  searchBasePath?: string;
}

const HeaderSearchInput: React.FC<Props> = ({
  className,
  searchQuery,
  searchBasePath,
}) => {
  const { t } = useTranslation();
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const logBiEvent = useBiLogger();

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const searchTerm = searchInputRef.current?.value?.trim();

      if (!searchTerm) {
        return; // dont allow empty search
      }

      const searchUrl = Url.searchLink(searchTerm, searchBasePath);
      const username = searchTerm.slice(1);
      const isCorrectUsername =
        searchTerm.startsWith('@') && USERNAME_REGEX.test(username);

      logBiEvent(
        BiData<SearchClickBiEvent>({
          evid: 710,
          search_terms: searchTerm,
          sectionname: 'popup',
        })
      );

      // Only search usernames if we are not on subdomain!
      if (isCorrectUsername) {
        const userLinkUrl = Url.userLink(username);
        // to test this locally do `.replace('.com', '.lan')`

        const http = new XMLHttpRequest();
        http.open('HEAD', userLinkUrl);
        http.onreadystatechange = function () {
          if (this.readyState === this.DONE) {
            const doesUserExists = this.status === 200;
            const redirectUrl = doesUserExists ? userLinkUrl : searchUrl;
            window.location.assign(redirectUrl);
          }
        };
        http.send();
      } else {
        window.location.assign(searchUrl);
      }
    },
    [logBiEvent, searchBasePath]
  );

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === 'Return') {
        event.preventDefault();
        event.stopPropagation();
        handleSubmit(event);
      }
    },
    [handleSubmit]
  );

  return (
    <form
      className={c(s['search-form'], className)}
      action={Url.searchLink()}
      onSubmit={handleSubmit}
    >
      <InputField
        id="search-input"
        defaultValue={searchQuery}
        ref={searchInputRef}
        autoComplete="off"
        className={s['input-field']}
        inputClassname={s['input']}
        placeholder={t('siteHeader.search')}
        aria-label={t('siteHeader.search')}
        onKeyDown={handleInputKeyDown}
        prefix={
          <SearchIcon
            size={IconSize.SMALLER}
            disableDefaultColors
            className={s['icon']}
            aria-hidden={true}
          />
        }
        onClick={() => {
          logBiEvent(
            BiData<TopNavClickBiEvent>({
              evid: 700,
              link_name: 'search',
            })
          );
        }}
      />
    </form>
  );
};
HeaderSearchInput.displayName = 'HeaderSearchInput';

export default HeaderSearchInput;
